import { useState } from 'react';
import TuneIcon from '@mui/icons-material/Tune';
import { Checkbox, FormControlLabel, IconButton, Menu, Stack, Typography } from '@mui/material';
import { WIDGETS, type DashboardWidgetKey } from '../widgets';

interface WidgetVisibilityMenuProps {
    visible: Record<DashboardWidgetKey, boolean>;
    access: Record<DashboardWidgetKey, boolean>;
    onToggle: (key: DashboardWidgetKey, visible: boolean) => void;
}

export const WidgetVisibilityMenu = ({
    visible,
    access,
    onToggle,
}: WidgetVisibilityMenuProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleToggle = (key: DashboardWidgetKey) => {
        onToggle(key, !visible[key]);
    };

    const accessibleWidgets = WIDGETS.filter((w) => access[w.key]);

    if (accessibleWidgets.length === 0) {
        return null;
    }

    return (
        <>
            <IconButton
                onClick={handleClick}
                size="small"
                sx={{
                    minHeight: 44,
                    minWidth: 44,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                aria-label="Widget visibility"
                title="Toggle widget visibility"
            >
                <TuneIcon />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Stack sx={{ p: 1.5, minWidth: 220, maxHeight: '70vh', overflowY: 'auto' }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 600,
                            color: '#6B7280',
                            mb: 1,
                            px: 1,
                        }}
                    >
                        Show widgets
                    </Typography>
                    {accessibleWidgets.map(({ key, label }) => (
                        <FormControlLabel
                            key={key}
                            control={
                                <Checkbox
                                    checked={visible[key]}
                                    onChange={() => handleToggle(key)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    {label}
                                </Typography>
                            }
                            sx={{
                                m: 0,
                                my: 0.5,
                                '& .MuiCheckbox-root': {
                                    minHeight: 40,
                                    minWidth: 40,
                                },
                            }}
                        />
                    ))}
                </Stack>
            </Menu>
        </>
    );
};

WidgetVisibilityMenu.displayName = 'WidgetVisibilityMenu';
